// src/pages/requisiciones/RequisicionPublicaPage.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Table, Form, Row, Col } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import {
  obtenerRequisicionPublica,
  responderRequisicionPublica,
  responderCertificadosPublico,
  responderInvitacionPublica
} from '../../services/requisiciones/requisicionService';
import { subirArchivoPublicoRequisicion, eliminarArchivoPublicoRequisicion, subirCertificadoPublicoRequisicion } from '../../api/common';
import MensajeCortesiaProveedor from '../../components/requisiciones/MensajeCortesiaProveedor';
import { urlSegura } from '../../utils/urlSegura';
import Swal from 'sweetalert2';
import logo from '../../img/Tinvaz_Logo.png';

const RequisicionPublicaPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [requisicion, setRequisicion] = useState(null);
  const [error, setError] = useState('');
  // TIN-23: cuando el backend responde 403 con code TOKEN_EXPIRADO, renderizamos
  // MensajeCortesiaProveedor SIN exponer datos de la requisición.
  const [tokenInvalido, setTokenInvalido] = useState(false);
  const [respondiendoInvitacion, setRespondiendoInvitacion] = useState(false);

  // Formulario de respuesta
  const [correoEnvio, setCorreoEnvio] = useState('');
  const [linkPropuesta, setLinkPropuesta] = useState('');
  const [observacion, setObservacion] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [archivoUrl, setArchivoUrl] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [respondida, setRespondida] = useState(false);

  // Certificados
  const [certArchivos, setCertArchivos] = useState([]);
  const [subiendoCert, setSubiendoCert] = useState(false);
  const [enviandoCerts, setEnviandoCerts] = useState(false);
  const [certLinkInput, setCertLinkInput] = useState('');

  useEffect(() => {
    cargarRequisicion();
  }, [token]);

  const cargarRequisicion = async () => {
    setLoading(true);
    setError('');
    setTokenInvalido(false);

    try {
      const response = await obtenerRequisicionPublica(token);

      if (response.status === 200 && response.data?.data) {
        const req = response.data.data;

        console.log('Requisición obtenida del API:', req);

        // Verificar si ya está cerrada o si ya respondió este proveedor
        if (req.estado === 'Cerrada') {
          setError('Esta requisición ya ha sido cerrada');
        } else if (req.yaRespondio) {
          setRespondida(true);
        }

        setRequisicion(req);
      } else {
        setError('Requisición no encontrada o link inválido');
      }
    } catch (error) {
      console.error('Error al cargar requisición:', error);
      // TIN-23: 403 con code TOKEN_EXPIRADO → mensaje de cortesía sin datos
      const statusCode = error?.response?.status;
      const errorCode = error?.response?.data?.code;
      if (statusCode === 403 && errorCode === 'TOKEN_EXPIRADO') {
        setTokenInvalido(true);
      } else {
        setError('Error al cargar la requisición. Verifique el link o intente más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  // TIN-23 CA-03: aceptar/rechazar invitación
  const handleResponderInvitacion = async (aceptar) => {
    const confirmacion = await Swal.fire({
      title: aceptar ? '¿Aceptar invitación?' : '¿Rechazar invitación?',
      text: aceptar
        ? 'Confirmas que vas a cotizar esta requisición.'
        : 'Si rechazas, perderás acceso a este proceso.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: aceptar ? 'Sí, aceptar' : 'Sí, rechazar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: aceptar ? '#198754' : '#dc3545',
    });
    if (!confirmacion.isConfirmed) return;

    setRespondiendoInvitacion(true);
    try {
      const response = await responderInvitacionPublica(token, aceptar);
      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: aceptar ? 'Invitación aceptada' : 'Invitación rechazada',
          text: aceptar
            ? 'Continúa el proceso para enviar tu cotización.'
            : 'Gracias por tu tiempo.',
          confirmButtonText: 'Entendido',
        });
        if (aceptar) {
          // Refrescar para mostrar el formulario de cotización
          cargarRequisicion();
        } else {
          // Si rechazó, próximo acceso será 403 — desactivar UI
          setTokenInvalido(true);
        }
      } else {
        Swal.fire('Error', response.data?.message || 'No se pudo registrar la respuesta.', 'error');
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const errorCode = error?.response?.data?.code;
      if (statusCode === 403 && errorCode === 'TOKEN_EXPIRADO') {
        setTokenInvalido(true);
        return;
      }
      Swal.fire('Error', error?.response?.data?.message || 'No se pudo registrar la respuesta.', 'error');
    } finally {
      setRespondiendoInvitacion(false);
    }
  };

  const handleArchivoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      Swal.fire('Error', 'El archivo no debe superar los 10MB', 'error');
      return;
    }

    const tiposPermitidos = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!tiposPermitidos.includes(file.type)) {
      Swal.fire('Error', 'Solo se permiten archivos PDF o Word', 'error');
      return;
    }

    setArchivo(file);
    setSubiendoArchivo(true);
    try {
      const response = await subirArchivoPublicoRequisicion(token, file);
      if (response.status === 200 && response.data?.url) {
        setArchivoUrl(response.data.url);
      } else {
        throw new Error('Error al subir archivo');
      }
    } catch (error) {
      console.error('Error al subir archivo:', error);
      Swal.fire('Error', 'No se pudo subir el archivo. Intente nuevamente.', 'error');
      setArchivo(null);
    } finally {
      setSubiendoArchivo(false);
      e.target.value = '';
    }
  };

  const handleEliminarArchivo = async () => {
    const confirmacion = await Swal.fire({
      title: '¿Eliminar archivo?',
      text: 'Se eliminará el archivo adjuntado. Podrá subir otro.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmacion.isConfirmed) return;
    try {
      await eliminarArchivoPublicoRequisicion(token, archivoUrl);
    } catch { /* igual limpiamos el estado */ }
    setArchivoUrl('');
    setArchivo(null);
  };

  const validarFormulario = () => {

    return true;
  };

  const handleEnviarRespuesta = async () => {
    if (!validarFormulario()) return;

    const result = await Swal.fire({
      title: '¿Enviar cotización?',
      text: '¿Está seguro de enviar esta información?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0d6efd'
    });

    if (!result.isConfirmed) return;

    setEnviando(true);
    try {
      const data = {
        correoEnvio: correoEnvio.trim() || undefined,
        archivo: archivoUrl || undefined,
        linkPropuesta: linkPropuesta.trim() || undefined,
        observacion: observacion.trim() || undefined
      };

      const response = await responderRequisicionPublica(token, data);
      if (response.status === 200) {
        Swal.fire({
          title: '¡Cotización Enviada!',
          text: 'Su respuesta ha sido registrada correctamente. Nos pondremos en contacto pronto.',
          icon: 'success',
          confirmButtonText: 'Entendido'
        });
        setRespondida(true);
      } else {
        Swal.fire('Error', response.data?.message || 'Error al enviar la respuesta', 'error');
      }
    } catch (error) {
      console.error('Error al enviar respuesta:', error);
      Swal.fire('Error', 'No se pudo enviar la respuesta. Intente nuevamente.', 'error');
    } finally {
      setEnviando(false);
    }
  };

  const handleSubirCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      Swal.fire('Error', 'El archivo no debe superar los 20MB', 'error');
      return;
    }
    setSubiendoCert(true);
    try {
      const res = await subirCertificadoPublicoRequisicion(token, file);
      if (res.status === 200 && res.data?.url) {
        setCertArchivos(prev => [...prev, { url: res.data.url, nombre: file.name }]);
      } else {
        throw new Error('Error al subir certificado');
      }
    } catch (error) {
      Swal.fire('Error', 'No se pudo subir el certificado. Inténtelo nuevamente.', 'error');
    } finally {
      setSubiendoCert(false);
      e.target.value = '';
    }
  };

  const handleEliminarCert = (url) => {
    setCertArchivos(prev => prev.filter(c => c.url !== url));
  };

  const handleAgregarCertLink = () => {
    const url = certLinkInput.trim();
    if (!url) return;
    // Basic URL validation
    try {
      new URL(url);
    } catch {
      Swal.fire('URL inválida', 'Ingrese una URL válida (ej: https://drive.google.com/...)', 'warning');
      return;
    }
    if (certArchivos.some(c => c.url === url)) {
      Swal.fire('Duplicado', 'Este enlace ya fue agregado', 'warning');
      return;
    }
    setCertArchivos(prev => [...prev, { url, nombre: url }]);
    setCertLinkInput('');
  };

  const handleEnviarCertificados = async () => {
    if (certArchivos.length === 0) {
      Swal.fire('Atención', 'Debe adjuntar al menos un certificado', 'warning');
      return;
    }
    const confirm = await Swal.fire({
      title: '¿Enviar certificados?',
      text: `Se enviarán ${certArchivos.length} certificado(s) de calibración.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#198754'
    });
    if (!confirm.isConfirmed) return;
    setEnviandoCerts(true);
    try {
      for (const cert of certArchivos) {
        await responderCertificadosPublico(token, cert.url);
      }
      Swal.fire('¡Enviado!', 'Certificados registrados correctamente.', 'success');
      setCertArchivos([]);
      cargarRequisicion();
    } catch (error) {
      Swal.fire('Error', 'No se pudieron registrar los certificados.', 'error');
    } finally {
      setEnviandoCerts(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando requisición...</p>
        </div>
      </div>
    );
  }

  // TIN-23 CA-05: mensaje de cortesía sin exponer datos de la requisición
  if (tokenInvalido) {
    return <MensajeCortesiaProveedor />;
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <img src={logo} alt="Logo" style={{ maxWidth: '200px', marginBottom: '30px' }} />
          <Alert variant="danger" className="mx-auto" style={{ maxWidth: '600px' }}>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  if (!requisicion) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">Requisición no encontrada</Alert>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-9">
          {/* HEADER */}
          <Card className="mb-4 w-100">
            <Card.Header className="bg-primary text-white text-center py-4 w-100">
              <h3 className="mb-0">Solicitud de Cotización</h3>
            </Card.Header>
              <Card.Body className="p-4 w-100">
                {/* EMPRESA */}
                <Row className="align-items-center mb-2">
                  <Col sm={3} className="text-center">
                    <img src={logo} alt="Logo" style={{ maxWidth: '120px', width: '100%' }} />
                  </Col>
                  <Col sm={9}>
                    <h5 className="mb-1 fw-bold">BIOLAB LABORATORIO METROLÓGICO S.A.S.</h5>
                    <p className="mb-0 text-muted small">NIT 900.419.208-1</p>
                    <p className="mb-0 text-muted small">CALLE 9 #16-09 — Cereté, Colombia</p>
                    <p className="mb-0 text-muted small">Tel: 3215873582</p>
                  </Col>
                </Row>
              </Card.Body>
          </Card>
          <div className="text-center mb-4">
            <p className="text-muted">
              Ha sido invitado a cotizar en una requisición de servicios. Por favor revise los detalles a continuación y envíe su cotización utilizando el formulario al final de la página.
            </p>
          </div>

          {/* INFO REQUISICIÓN */}
          <Card className="mb-4 w-100">
            <Card.Header className="bg-primary text-white w-100">
              <h5 className="mb-0">
                <i className="bi bi-clipboard-check me-2"></i>
                Requisición #{requisicion.consecutivo}
              </h5>
            </Card.Header>
            <Card.Body className='w-100'>
              <h5 className="mb-3">Titulo: {requisicion.titulo}</h5>
              <Row>
                <Col sm={6}>
                  <p className="mb-2">
                    <strong>Fecha:</strong>{' '}
                    {moment(requisicion.fechaCreacion).format('DD/MM/YYYY')}
                  </p>
                </Col>
              </Row>
              {requisicion.observacionComercial && (
                <Alert variant="info" className="mt-3 mb-0">
                  <strong>Observaciones comerciales:</strong><br />
                  {requisicion.observacionComercial}
                </Alert>
              )}
              {/* TIN-23: observacionGeneral visible al proveedor (decisión PO 2026-06-09) */}
              {requisicion.observacionGeneral && (
                <Alert variant="info" className="mt-3 mb-0">
                  <strong>Observación general:</strong><br />
                  {requisicion.observacionGeneral}
                </Alert>
              )}
              {/* TIN-23: tipo Servicio_General */}
              {requisicion.tipoRequisicion === 'Servicio_General' && requisicion.tipoServicio && (
                <Alert variant="secondary" className="mt-3 mb-0">
                  <strong>Tipo de servicio:</strong> {requisicion.tipoServicio}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* TIN-23 CA-03: Aceptar/Rechazar invitación */}
          {requisicion.proveedor?.estadoEnvio === 'Enviado' && (
            <Card className="mb-4 w-100 border-warning">
              <Card.Header className="bg-warning text-dark w-100">
                <h6 className="mb-0">
                  <i className="bi bi-envelope-check me-2"></i>
                  Responder Invitación
                </h6>
              </Card.Header>
              <Card.Body className="w-100">
                <p className="mb-3">
                  Por favor confirma si vas a participar en este proceso de cotización.
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="success"
                    onClick={() => handleResponderInvitacion(true)}
                    disabled={respondiendoInvitacion}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Aceptar invitación
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => handleResponderInvitacion(false)}
                    disabled={respondiendoInvitacion}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Rechazar invitación
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* TIN-23: banner de devolución (visible al ganador en estado Devolucion) */}
          {requisicion.estado === 'Devolucion' && requisicion.ultimaDevolucion?.observacion && (
            <Alert variant="warning" className="mb-4">
              <h6 className="alert-heading">
                <i className="bi bi-arrow-counterclockwise me-2"></i>
                Devolución de la entrega
              </h6>
              <p className="mb-2">
                Tu entrega anterior fue devuelta con la siguiente observación:
              </p>
              <blockquote
                className="ps-3 mb-0"
                style={{ borderLeft: '3px solid #ffc107', fontStyle: 'italic' }}
              >
                {requisicion.ultimaDevolucion.observacion}
              </blockquote>
              <hr />
              <p className="mb-0 small">
                Por favor sube nuevos certificados desde la sección al final de esta página.
              </p>
            </Alert>
          )}

          {/* ITEMS — TIN-23: tabla condicional según tipoRequisicion */}
          <Card className="mb-4 w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">
                <i className="bi bi-list-check me-2"></i>
                Items a Cotizar
              </h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <div className="table-responsive">
                {requisicion.tipoRequisicion === 'Servicio_General' ? (
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '60px' }}>#</th>
                        <th>Descripción del servicio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requisicion.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <small style={{ whiteSpace: 'pre-wrap' }}>
                              {item.descripcionServicio || '—'}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>Tipo Actividad</th>
                        <th>Equipo</th>
                        <th>Requerimientos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requisicion.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.tipoActividad || '—'}</td>
                          <td>
                            {item.snapshotPatron?.item ? (
                              <small>
                                <strong>{item.snapshotPatron.item}</strong><br />
                                {item.snapshotPatron.marca} {item.snapshotPatron.modelo}<br />
                                S/N: {item.snapshotPatron.serie}
                              </small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            <small style={{ whiteSpace: 'pre-wrap' }}>{item.snapshotPatron?.requerimientos || '—'}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* FORMULARIO DE RESPUESTA — solo si aún en etapa de cotización */}
          {!['Orden_Enviada_Proveedor','Certificados_Pendientes','Certificados_Recibidos','Cerrada'].includes(requisicion.estado) && (
            respondida ? (
            <Alert variant="success" className="text-center">
              <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '2rem' }}></i>
              <h5 className="mt-2">¡Cotización Enviada!</h5>
              <p className="mb-0">
                Su respuesta ha sido registrada. Nos pondremos en contacto pronto.
              </p>
            </Alert>
          ) : (
            <Card className='w-100'>
              <Card.Header className="bg-success text-white w-100">
                <h6 className="mb-0">
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>
                  Enviar Cotización
                </h6>
              </Card.Header>
              <Card.Body className='w-100'>
                <Form>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          Puedes enviar tu cotización adjuntando un archivo o un enlace a tu propuesta en línea.
                          <small className="text-muted d-block">También la puedes enviar al email: mddavid.d@gmail.com.</small>
                        </Form.Label>
                      </Form.Group>
                    </Col>
                    <Col xs={12}> {/* Columna completa que el proveedor pueda adjuntar link de la propuesta value={linkPropuesta} */}
                      <Form.Group>
                        <Form.Label>
                          Link a propuesta en línea (opcional)
                        </Form.Label>
                        <Form.Control
                          type="url"
                          value={linkPropuesta}
                          onChange={(e) => setLinkPropuesta(e.target.value)}
                          placeholder="https://drive.google.com/..."
                          disabled={enviando}
                        />
                        <Form.Text className="text-muted">
                          Ingrese el enlace a su propuesta en línea.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Adjuntar cotización (PDF o Word)</Form.Label>
                        {!archivoUrl ? (
                          <>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleArchivoChange}
                                disabled={enviando || subiendoArchivo}
                                style={{ maxWidth: 360 }}
                              />
                              {subiendoArchivo && <Spinner animation="border" size="sm" />}
                            </div>
                            {subiendoArchivo && (
                              <small className="text-primary mt-1 d-block">Subiendo archivo...</small>
                            )}
                          </>
                        ) : (
                          <Alert variant="success" className="d-flex align-items-center justify-content-between mb-0 py-2">
                            <span>
                              <i className="bi bi-check-circle-fill me-2"></i>
                              <a href={archivoUrl} target="_blank" rel="noopener noreferrer">Ver archivo adjunto</a>
                            </span>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ms-3"
                              onClick={handleEliminarArchivo}
                              disabled={enviando}
                            >
                              <i className="bi bi-trash"></i> Eliminar
                            </Button>
                          </Alert>
                        )}
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Observaciones (opcional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={observacion}
                          onChange={(e) => setObservacion(e.target.value)}
                          placeholder="Información adicional sobre su cotización..."
                          disabled={enviando}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Alert variant="info" className="mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Nota:</strong> Debe proporcionar al menos un correo electrónico 
                        o adjuntar un archivo con su cotización.
                      </Alert>
                    </Col>

                    <Col xs={12} className="text-end">
                      <Button
                        variant="success"
                        size="lg"
                        onClick={handleEnviarRespuesta}
                        disabled={enviando || subiendoArchivo}
                        className="px-5"
                      >
                        {enviando ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send me-2"></i>
                            Enviar Cotización
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          ))}

          {/* ORDEN DE COMPRA — banner visible cuando existe */}
          {requisicion.ordenCompra?.archivo && (
            <Alert variant="primary" className="mb-4 d-flex align-items-center gap-3">
              <i className="bi bi-file-earmark-ruled fs-4"></i>
              <div>
                <strong>Orden de Compra recibida</strong>
                <div>
                  <a href={urlSegura(requisicion.ordenCompra.archivo) || '#'} target="_blank" rel="noopener noreferrer">
                    <i className="bi bi-download me-1"></i>Descargar Orden de Compra
                  </a>
                </div>
              </div>
            </Alert>
          )}

          {/* SECCIÓN CERTIFICADOS — visible si la OC fue enviada o si la entrega
              fue devuelta (TIN-23: estado Devolucion permite re-entrega del ganador) */}
          {['Orden_Enviada_Proveedor', 'Certificados_Pendientes', 'Devolucion'].includes(requisicion.estado) && (
            <Card className="mb-4 w-100">
              <Card.Header className="bg-success text-white py-3 w-100">
                <h5 className="mb-0">
                  <i className="bi bi-patch-check me-2"></i>
                  Certificados de Calibración
                </h5>
              </Card.Header>
              <Card.Body className="p-4 w-100">
                <p className="text-muted mb-3">
                  La orden de compra fue enviada. Por favor adjunte los certificados de calibración.
                  Puede subir archivos directamente <strong>o</strong> pegar un enlace de descarga (Google Drive, Dropbox, etc.).
                </p>

                {/* Lista de certificados agregados */}
                {certArchivos.length > 0 && (
                  <div className="mb-3">
                    {certArchivos.map((cert, i) => {
                      // TIN-23: solo renderizar el <a> si la URL es segura (http/https).
                      // El usuario puede pegar links externos en este input → defensa contra
                      // protocolos javascript: / data: en hrefs.
                      const safeUrl = urlSegura(cert.url);
                      return (
                      <Alert key={cert.url} variant="success" className="d-flex align-items-center justify-content-between py-2 mb-2">
                        <span>
                          <i className="bi bi-file-earmark-check me-2"></i>
                          {safeUrl ? (
                            <a href={safeUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                              {cert.nombre?.startsWith('http') ? `Enlace ${i + 1}` : (cert.nombre || `Certificado ${i + 1}`)}
                            </a>
                          ) : (
                            <span style={{ wordBreak: 'break-all' }}>{cert.nombre || `Certificado ${i + 1}`}</span>
                          )}
                        </span>
                        <Button variant="outline-danger" size="sm" onClick={() => handleEliminarCert(cert.url)} disabled={enviandoCerts}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Alert>
                    );
                    })}
                  </div>
                )}

                {/* Opción 1: Subir archivo */}
                <div className="border rounded p-3 mb-3 bg-light">
                  <p className="fw-semibold mb-2"><i className="bi bi-upload me-1"></i> Subir archivo (PDF, DOC, ZIP, etc.)</p>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx,.zip,.rar,.7z,.xls,.xlsx"
                      onChange={handleSubirCert}
                      disabled={subiendoCert || enviandoCerts}
                      style={{ maxWidth: 380 }}
                    />
                    {subiendoCert && <><Spinner animation="border" size="sm" /><small className="text-primary">Subiendo...</small></>}
                  </div>
                </div>

                {/* Opción 2: Pegar enlace */}
                <div className="border rounded p-3 mb-3 bg-light">
                  <p className="fw-semibold mb-2"><i className="bi bi-link-45deg me-1"></i> O pegar enlace de descarga</p>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="url"
                      placeholder="https://drive.google.com/file/..."
                      value={certLinkInput}
                      onChange={(e) => setCertLinkInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAgregarCertLink()}
                      disabled={enviandoCerts}
                      style={{ maxWidth: 400 }}
                    />
                    <Button
                      variant="outline-success"
                      onClick={handleAgregarCertLink}
                      disabled={!certLinkInput.trim() || enviandoCerts}
                    >
                      <i className="bi bi-plus-lg me-1"></i>Agregar
                    </Button>
                  </div>
                  <small className="text-muted">Google Drive, Dropbox, OneDrive, WeTransfer, etc.</small>
                </div>

                <Button
                  variant="success"
                  size="lg"
                  onClick={handleEnviarCertificados}
                  disabled={certArchivos.length === 0 || enviandoCerts || subiendoCert}
                  className="px-4"
                >
                  {enviandoCerts ? (
                    <><Spinner animation="border" size="sm" className="me-2" />Enviando...</>
                  ) : (
                    <><i className="bi bi-send me-2"></i>Enviar {certArchivos.length} certificado{certArchivos.length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Mensaje si ya envió certificados */}
          {requisicion.estado === 'Certificados_Recibidos' && (
            <Alert variant="success" className="mb-4">
              <i className="bi bi-patch-check-fill me-2"></i>
              Los certificados de calibración han sido recibidos correctamente. ¡Gracias!
            </Alert>
          )}

          {/* FOOTER */}
          <div className="text-center mt-4">
            <small className="text-muted">
              © {moment().format('YYYY')} TINVAZ.COM - Todos los derechos reservados.
            </small>
          </div>
        </div>
      </div>
    </div> 
  );
};

export default RequisicionPublicaPage;
